import { useState, useCallback, useMemo } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import Tree, { RawNodeDatum, CustomNodeElementProps } from 'react-d3-tree'
import { BookOpen, FolderOpen, FileText, Play, ChevronRight, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react'
import { clsx } from 'clsx'
import { Dialog, DialogContent } from '@quebon/ui/components/dialog'

// 계층 구조 노드 타입
export interface ContentHierarchyNode {
  id: number
  title: string
  bareTitle?: string | null
  type: string // curriculum, course, module, lesson
  description?: string | null
  thumbnailUrl?: string | null
  sequenceOrder?: number
  estimatedDurationMinutes?: number | null
  children?: ContentHierarchyNode[]
}

// 트리 뷰어 props
interface CurriculumTreeViewerProps {
  data: ContentHierarchyNode
  isOpen: boolean
  onClose: () => void
  title?: string
}

// 노드 타입별 아이콘 및 색상
const nodeConfig: Record<string, { icon: typeof BookOpen; color: string; bgColor: string; borderColor: string }> = {
  curriculum: { icon: BookOpen, color: 'text-purple-600', bgColor: 'bg-purple-100', borderColor: 'border-purple-400' },
  course: { icon: FolderOpen, color: 'text-blue-600', bgColor: 'bg-blue-100', borderColor: 'border-blue-400' },
  module: { icon: FileText, color: 'text-green-600', bgColor: 'bg-green-100', borderColor: 'border-green-400' },
  lesson: { icon: Play, color: 'text-orange-600', bgColor: 'bg-orange-100', borderColor: 'border-orange-400' },
}

// ContentHierarchyNode를 react-d3-tree 형식으로 변환
// depth 파라미터를 추가하여 깊이 추적
function convertToTreeData(node: ContentHierarchyNode, depth: number = 0): RawNodeDatum {
  // depth가 2 이상이면 초기에 접힌 상태로 설정
  const shouldCollapse = depth >= 2
  
  return {
    name: node.bareTitle || node.title,
    attributes: {
      type: node.type,
      id: node.id.toString(),
      fullTitle: node.title,
      duration: node.estimatedDurationMinutes?.toString() || '',
    },
    children: node.children?.map(child => convertToTreeData(child, depth + 1)) || [],
    ...(shouldCollapse && { collapsed: true }),
  }
}

// 커스텀 노드 렌더러
function renderCustomNode({ nodeDatum, toggleNode }: CustomNodeElementProps) {
  const type = (nodeDatum.attributes?.type as string) || 'lesson'
  const config = nodeConfig[type] || nodeConfig.lesson
  const Icon = config.icon
  const hasChildren = nodeDatum.children && nodeDatum.children.length > 0
  const isCollapsed = nodeDatum.__rd3t?.collapsed

  return (
    <g>
      {/* 노드 배경 */}
      <foreignObject
        x={-100}
        y={-30}
        width={200}
        height={60}
        style={{ overflow: 'visible' }}
      >
        <div
          className={clsx(
            'flex items-center gap-2 px-3 py-2 rounded-lg border-2 shadow-md cursor-pointer transition-all hover:shadow-lg hover:scale-105',
            config.bgColor,
            config.borderColor
          )}
          onClick={toggleNode}
        >
          <div className={clsx('p-1.5 rounded-md bg-white/80', config.color)}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">
              {type}
            </div>
            <div className="text-sm font-semibold text-gray-900 truncate" title={nodeDatum.name}>
              <ContentMetadataText text={nodeDatum.name} />
            </div>
          </div>
          {hasChildren && (
            <ChevronRight 
              className={clsx(
                'w-4 h-4 text-gray-400 transition-transform',
                !isCollapsed && 'rotate-90'
              )} 
            />
          )}
        </div>
      </foreignObject>
    </g>
  )
}

export default function CurriculumTreeViewer({ data, isOpen, onClose, title }: CurriculumTreeViewerProps) {
  const [zoom, setZoom] = useState(0.7)
  const [translate, setTranslate] = useState({ x: 300, y: 100 })

  // 데이터 변환
  const treeData = useMemo(() => convertToTreeData(data), [data])

  // 줌 컨트롤
  const handleZoomIn = useCallback(() => setZoom((z) => Math.min(z + 0.1, 2)), [])
  const handleZoomOut = useCallback(() => setZoom((z) => Math.max(z - 0.1, 0.3)), [])
  const handleReset = useCallback(() => {
    setZoom(0.7)
    setTranslate({ x: 300, y: 100 })
  }, [])

  // 노드 수 계산
  const nodeCount = useMemo(() => {
    let count = 0
    const countNodes = (node: ContentHierarchyNode) => {
      count++
      node.children?.forEach(countNodes)
    }
    countNodes(data)
    return count
  }, [data])

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
    <DialogContent className="max-w-[calc(100%-2rem)] md:max-w-[calc(100%-4rem)] lg:max-w-[calc(100%-6rem)] max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-4rem)] lg:max-h-[calc(100vh-6rem)] h-[calc(100vh-2rem)] md:h-[calc(100vh-4rem)] lg:h-[calc(100vh-6rem)] p-0 gap-0">
      <div className="flex flex-col h-full">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white flex-shrink-0">
        <div className="flex items-center gap-3">
          <BookOpen className="w-6 h-6" />
          <div>
            <h2 className="text-xl font-bold"><ContentMetadataText text={title || '커리큘럼 구조'} /></h2>
            <p className="text-sm text-purple-200">
              총 {nodeCount}개 항목 • 노드를 클릭하여 펼치기/접기
            </p>
          </div>
        </div>
      </div>

      {/* 범례 */}
      <div className="flex items-center gap-6 px-6 py-3 bg-gray-50 border-b border-gray-200 flex-shrink-0">
        {Object.entries(nodeConfig).map(([type, config]) => {
          const Icon = config.icon
          return (
            <div key={type} className="flex items-center gap-2">
              <div className={clsx('p-1 rounded', config.bgColor)}>
                <Icon className={clsx('w-3 h-3', config.color)} />
              </div>
              <span className="text-xs text-gray-600 capitalize">{type}</span>
            </div>
          )
        })}
      </div>

      {/* 줌 컨트롤 */}
      <div className="absolute top-32 right-8 z-10 flex flex-col gap-2">
        <button
          onClick={handleZoomIn}
          className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors"
          title="확대"
        >
          <ZoomIn className="w-5 h-5 text-gray-600" />
        </button>
        <button
          onClick={handleZoomOut}
          className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors"
          title="축소"
        >
          <ZoomOut className="w-5 h-5 text-gray-600" />
        </button>
        <button
          onClick={handleReset}
          className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors"
          title="초기화"
        >
          <Maximize2 className="w-5 h-5 text-gray-600" />
        </button>
        <div className="text-xs text-center text-gray-500 mt-1">
          {Math.round(zoom * 100)}%
        </div>
      </div>

      {/* 트리 뷰 */}
      <div className="flex-1 bg-gradient-to-br from-slate-50 to-slate-100">
        <Tree
          data={treeData}
          orientation="horizontal"
          pathFunc="step"
          translate={translate}
          zoom={zoom}
          initialDepth={2}
          onUpdate={({ zoom: newZoom, translate: newTranslate }) => {
            setZoom(newZoom)
            setTranslate(newTranslate)
          }}
          renderCustomNodeElement={renderCustomNode}
          nodeSize={{ x: 250, y: 80 }}
          separation={{ siblings: 1.2, nonSiblings: 1.5 }}
          enableLegacyTransitions
          transitionDuration={300}
          pathClassFunc={() => 'stroke-gray-300 stroke-2'}
          rootNodeClassName="node__root"
          branchNodeClassName="node__branch"
          leafNodeClassName="node__leaf"
        />
      </div>

      {/* 푸터 - 도움말 */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex-shrink-0">
        <span className="font-medium">Tip:</span> 마우스 드래그로 이동, 스크롤로 확대/축소, 노드 클릭으로 펼치기/접기
      </div>
      </div>
    </DialogContent>
    </Dialog>
  )
}
